import { memo, useCallback, useMemo } from "react";

import CustomRadioButton from "@/components/Button/CustomRadioButton";
import Icon from "@/components/Icon/Icon";
import { HStack } from "@/components/Layout";
import { RadioGroup } from "@/components/ui/radio-group";

import type { FieldRenderInputProps } from "../../types";
import { getAttributeOptions } from "../../utils/option-utils";

import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";

/** 選択オプションの型 */
interface ChangeRadioOption {
  value: string;
  label: string;
}

interface ChangeRadioFieldProps extends BaseFieldProps {
  options?: ChangeRadioOption[];
}

/**
 * ラジオボタン入力フィールドコンポーネント
 * 複数の選択肢から一つを選択するためのフィールド
 */
export const ChangeRadioField = memo(
  ({
    fieldId,
    field,
    readOnly,
    disabled,
    className,
    options,
    "data-testid": testId,
  }: ChangeRadioFieldProps) => {
    // 属性から選択肢を取得
    const radioOptions = useMemo(() => {
      // オプションが提供されている場合はそれを使用
      if (options && options.length > 0) {
        return options;
      }

      if (field) {
        return getAttributeOptions(field);
      }

      return [];
    }, [field, options]);

    // 入力レンダリング関数
    const renderInput = useCallback(
      (props: FieldRenderInputProps) => {
        const handleValueChange = (value: string) => {
          props.onChange(value);
        };

        return (
          <HStack className="items-center justify-center w-full gap-4">
            {/* 現在の値（読み取り専用のテキスト表示） */}
            <div className="flex-1 justify-start min-w-0">
              <RadioGroup
                value={props.previousValue?.toString()}
                disabled={true}
              >
                {radioOptions.map((option: ChangeRadioOption) => (
                  <CustomRadioButton
                    key={`${option.value}-readonly`}
                    value={`${option.value}-readonly`}
                    checked={props.previousValue === option.value}
                    label={option.label}
                    onChange={() => {}}
                  />
                ))}
              </RadioGroup>
            </div>

            {/* Arrow Icon */}
            <div className="items-center shrink-0 self-center pr-3 pl-3">
              <Icon
                iconName="ArrowBigRightIcon"
                className="w-4 h-4 text-primary-on-neutral-fill"
              />
            </div>

            {/* 新しい値の入力 */}
            <div className="flex-1 min-w-0">
              <RadioGroup
                value={props.value?.toString()}
                onValueChange={handleValueChange}
                onFocus={props.onFocus}
                onBlur={props.onBlur}
                disabled={readOnly}
              >
                {radioOptions.map((option: ChangeRadioOption) => (
                  <CustomRadioButton
                    key={option.value}
                    value={option.value}
                    checked={props.value === option.value}
                    label={option.label}
                    onChange={handleValueChange}
                  />
                ))}
              </RadioGroup>
            </div>
          </HStack>
        );
      },
      [radioOptions, readOnly],
    );

    // BaseFieldを使用してラジオフィールドをレンダリング
    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        layout="vertical" // UI 的にラベルと入力フィールドが縦並びじゃないとしんどいので
        readOnly={readOnly}
        disabled={disabled}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

ChangeRadioField.displayName = "ChangeRadioField";
